// ── A. INDIVIDUAL COVERAGE NEED — how valuable is a FARM-triggered guarantee? ─
//
// This module answers one question and is deliberately blind to the county.
//
//     On this farm's own record, how much revenue protection does each 5-point
//     band of individual coverage actually deliver?
//
// It takes NO area argument. That is a structural refusal, not a convention:
// the previous engine let a correlation against an area index decide the
// underlying individual coverage level, which conflates two different
// questions — how much individual downside exists, and how well something else
// substitutes for it. `areaFit.js` answers the second. The decision layer
// combines them; neither may quietly become the other.
//
// ── WHAT A BAND IS WORTH, AND WHY IT IS COMPUTED THIS WAY ────────────────────
//
// An RP guarantee at level L is `APH x L x projected price` per acre. The
// indemnity is the shortfall below it. So the band between two levels is the
// slice of the shortfall that sits between them:
//
//     band(lo,hi) = price x [ max(0, APH*hi - y) - max(0, APH*lo - y) ]
//
// Averaged over the farm's own yield years, that is the expected annual
// indemnity the band would have paid. It is the honest denominator for "is this
// band worth its premium", and it is measured per farm rather than assumed.
//
// ── ⚠ THE BASIS IS NOW REVENUE WHERE THE PRICES ARE KNOWN (2026-09-23) ───────
// This header used to read: "we do not hold a farm-level harvest-price history, so
// revenue is approximated as `yield x projected price`." That reason was a CATEGORY
// ERROR. **RP does not settle on a farm-level price** — it settles on RMA's published
// projected and harvest prices, per (crop year, state, practice, type), and this
// database already holds them: 20,334 wheat rows, 2015-2026 complete on both, all five
// states, and county does not matter (378 groups, zero where counties disagree).
//
// Kevin, 2026-09-23: *"decide what their optimal coverage would've been across those 10
// years based on their yield AND PRICE MOVEMENT from guarantee to harvest price."*
//
// Given a `priceHistory` and a `revenuePlan`, every band is replayed at each year's own
// two prices through `revenueBacktest.js` and `basis` reads "revenue". Without either it
// falls back to the yield-only estimate and still says so — the label was always the
// point, and it still is.
//
// HOW MUCH THIS MATTERS, measured on a 7-year fixture at 0->80%:
//     yield-only, flat $6.00 .... $22.29/ac
//     revenue, YP ............... $27.86/ac
//     revenue, RP ............... $41.70/ac   <- 87% more than the yield model credited
// The yield model understates RP by nearly half, and always in the direction that makes
// coverage look not worth buying — which is why so little ever cleared its premium.
// ⚠ For YP the two bases must agree TO THE CENT (both settle in bushels at the projected
// price); `check-wheat-revenue` asserts that equality rather than assuming it.
//
// ⚠ AND THE SAMPLE IS SMALL. A wheat unit in this book carries a median of 7
// yield years. `MIN_YEARS_FOR_EXPECTATION` = 5; below it the expectation is
// null and the reason is stated, never a number computed from three years and
// presented beside a premium to two decimals. On the revenue basis the floor is
// on years that HAVE a published price pair, not on years on file.

import { bandExpectedIndemnityRevenue } from "./revenueBacktest.js";

// Minimum yield years before an expected-indemnity figure is reported at all.
export const MIN_YEARS_FOR_EXPECTATION = 5;
// Below this many years, even the descriptive statistics (CV, loss frequency)
// are withheld — three points do not describe a distribution.
export const MIN_YEARS_FOR_STATS = 4;

// Descriptive labels. These grade the FARM's own downside, never the fit.
export const NEED = {
  HIGH: "HIGH_INDIVIDUAL_NEED",
  MODERATE: "MODERATE_INDIVIDUAL_NEED",
  LOW: "LOW_INDIVIDUAL_NEED",
  UNKNOWN: "INDIVIDUAL_NEED_UNKNOWN",
};
export const NEED_LABEL = {
  [NEED.HIGH]: "High",
  [NEED.MODERATE]: "Moderate",
  [NEED.LOW]: "Low",
  [NEED.UNKNOWN]: "Not enough history",
};

const r4 = (n) => (n == null || !isFinite(n) ? null : Math.round(n * 10000) / 10000);
const r2 = (n) => (n == null || !isFinite(n) ? null : Math.round(n * 100) / 100);

// Coefficient of variation of a [{yr,y}] series, in percent.
export function cvOf(series) {
  const v = (series || []).map((o) => o && o.y).filter((y) => y != null && isFinite(y));
  if (v.length < MIN_YEARS_FOR_STATS) return null;
  const m = v.reduce((a, b) => a + b, 0) / v.length;
  const va = v.reduce((a, b) => a + (b - m) * (b - m), 0) / (v.length - 1);
  return m > 0 ? Math.sqrt(va) / m * 100 : null;
}

// How often the series fell below `level x APH`.
export function lossFreqAt(series, aph, level) {
  const s = (series || []).filter((o) => o && o.y != null && isFinite(o.y));
  if (s.length < MIN_YEARS_FOR_STATS || !(+aph > 0)) return null;
  const t = level * +aph;
  return s.filter((o) => o.y < t).length / s.length;
}

// Mean DEPTH of the shortfall in the years it occurred, as a fraction of the
// trigger. Frequency and severity are different facts: a farm that is 2% short
// three times in nine years and one that is 40% short three times have the same
// loss frequency and very different need.
export function lossSeverityAt(series, aph, level) {
  const s = (series || []).filter((o) => o && o.y != null && isFinite(o.y));
  if (s.length < MIN_YEARS_FOR_STATS || !(+aph > 0)) return null;
  const t = level * +aph;
  const short = s.filter((o) => o.y < t).map((o) => (t - o.y) / t);
  if (!short.length) return 0;
  return short.reduce((a, b) => a + b, 0) / short.length;
}

// The worst year on record, as a fraction of APH. Descriptive, and the one
// number an agent tends to want first.
export function worstYearFrac(series, aph) {
  const s = (series || []).filter((o) => o && o.y != null && isFinite(o.y));
  if (!s.length || !(+aph > 0)) return null;
  return Math.min(...s.map((o) => o.y / +aph));
}

// ── the band expectation ─────────────────────────────────────────────────────
//
// Expected annual indemnity per acre from the coverage band (lo, hi], on this
// farm's own yield record, priced at the projected price. `lo` may be 0 to get
// the whole guarantee's expectation.
export function bandExpectedIndemnity({ series, aph, price, lo, hi }) {
  const s = (series || []).filter((o) => o && o.y != null && isFinite(o.y) && o.y > 0);
  const A = +aph, P = +price;
  if (!(A > 0) || !(P > 0) || !(hi > lo)) return null;
  if (s.length < MIN_YEARS_FOR_EXPECTATION) return null;
  const gLo = A * lo, gHi = A * hi;
  let sum = 0, hits = 0;
  for (const o of s) {
    const above = Math.max(0, gHi - o.y);
    const below = Math.max(0, gLo - o.y);
    const slice = above - below;         // bushels of shortfall inside the band
    if (slice > 0) hits++;
    sum += slice * P;
  }
  return { perAcre: r4(sum / s.length), yearsPaying: hits, years: s.length };
}

// ── the assessment ───────────────────────────────────────────────────────────
//
// NOTE THE SIGNATURE: no area rows, no county, no correlation. Adding one would
// make this the same question as `areaFit`, and `check-wheat-recommend` asserts
// the parameter list to keep it that way.
export function individualNeed({
  series = [], aph, price, ppAcres = null, insuredAcres = null,
  unitStructure = null, aphProvenance = null,
  // ── THE REVENUE BASIS (2026-09-23) ──────────────────────────────────────────
  // `priceHistory` is { [cropYear]: { projected, harvest } } — RMA's own published
  // pair per (year, state, practice, type), which this database already holds for
  // 2015-2026 on all five states. `revenuePlan` is RP / RP-HPE / YP.
  // Given both, every band is replayed at each year's OWN two prices and the result
  // is a revenue simulation; without them the yield-only estimate stands and says so.
  // Measured on a 7-year fixture: RP 0->80% pays $41.70/ac where the yield model
  // credited $22.29 — the yield model understates RP by nearly half, which is the
  // direction that makes coverage look not worth buying.
  priceHistory = null, revenuePlan = null,
}) {
  const A = +aph, P = +price;
  const s = (series || []).filter((o) => o && o.y != null && isFinite(o.y) && o.y > 0);
  const years = s.length;

  const reasons = [];
  if (years < MIN_YEARS_FOR_STATS) reasons.push(years + " yield year" + (years === 1 ? "" : "s") + " on file (need " + MIN_YEARS_FOR_STATS + " to describe the distribution)");
  else if (years < MIN_YEARS_FOR_EXPECTATION) reasons.push(years + " yield years — enough to describe, not enough to price a band (need " + MIN_YEARS_FOR_EXPECTATION + ")");

  // The revenue basis applies only with BOTH a price history and a settlement rule.
  // Either alone is not enough and neither is guessed — an unknown plan defaults to
  // nothing rather than to RP, because RP is the most generous of the three and
  // assuming it would overstate what coverage has been worth.
  const useRevenue = !!(priceHistory && revenuePlan);
  const band = (lo, hi) => useRevenue
    ? bandExpectedIndemnityRevenue({ series: s, aph: A, lo, hi, prices: priceHistory,
                                     plan: revenuePlan, minYears: MIN_YEARS_FOR_EXPECTATION })
    : bandExpectedIndemnity({ series: s, aph: A, price: P, lo, hi });

  const cv = cvOf(s);
  const lf80 = lossFreqAt(s, A, 0.80);
  const lf85 = lossFreqAt(s, A, 0.85);
  const sev80 = lossSeverityAt(s, A, 0.80);
  const worst = worstYearFrac(s, A);

  // PREVENT PLANT is reported where it is measurable and named where it is not.
  // Lowering the underlying coverage level lowers the PP guarantee with it, so a
  // farm carrying real PP exposure has a reason to hold coverage that a yield
  // series alone does not show.
  const ppFrac = (ppAcres != null && insuredAcres != null && +insuredAcres > 0)
    ? r4(+ppAcres / +insuredAcres) : null;
  if (ppFrac == null) reasons.push("prevent-plant exposure not measurable for this unit");

  // The grade describes the FARM, on its own record. It is deliberately coarse:
  // it labels the evidence, and the money decision is made against marginal cost
  // in coverageRecommend.js rather than against this word.
  let need;
  if (cv == null && lf80 == null) need = NEED.UNKNOWN;
  else if ((lf80 != null && lf80 >= 0.30) || (sev80 != null && sev80 >= 0.25) || (worst != null && worst <= 0.60)) need = NEED.HIGH;
  else if ((lf80 != null && lf80 >= 0.12) || (cv != null && cv >= 22) || (worst != null && worst <= 0.78)) need = NEED.MODERATE;
  else need = NEED.LOW;

  return {
    need, needLabel: NEED_LABEL[need],
    yearsObserved: years, usable: years >= MIN_YEARS_FOR_STATS,
    // The cleaned series itself, so a caller can price an ARBITRARY band
    // (an incumbent baseline needs pairs the fixed set does not carry)
    // through the SAME function rather than re-filtering the history.
    series: s,
    priceable: years >= MIN_YEARS_FOR_EXPECTATION,
    cv: r2(cv), lossFreq80: lf80, lossFreq85: lf85, lossSeverity80: r4(sev80),
    worstYearFrac: r4(worst),
    ppAcres: ppAcres != null ? +ppAcres : null, ppFrac,
    unitStructure: unitStructure || null,
    aphProvenance: aphProvenance || null,
    aph: A > 0 ? A : null, price: P > 0 ? P : null,
    // Every band this decision can reach, priced on the farm's own record.
    // ⚠ ONE BAND FUNCTION IS CHOSEN FOR ALL THREE, NEVER MIXED. Comparing a
    // revenue-basis band against a yield-basis one is comparing two different
    // quantities, and the whole decision is a subtraction between bands.
    bands: {
      "75-80": band(0.75, 0.80),
      "80-85": band(0.80, 0.85),
      "0-85": band(0, 0.85),
    },
    basis: useRevenue ? "revenue" : "yield_only",
    basisNote: useRevenue
      ? "Expected indemnities are this farm's own yield record replayed at each year's published projected and harvest price, settled as " + revenuePlan + "."
      : "Expected indemnities are this farm's own yield record priced at the projected price. RP's harvest-price provision is not modelled, so a short crop in a rising market would pay more than shown."
        + (revenuePlan && !priceHistory ? " No published price history was supplied for this unit." : "")
        + (priceHistory && !revenuePlan ? " The insurance plan could not be established, so the settlement rule is unknown." : ""),
    reasons,
  };
}

// One checkable sentence about the FARM. Says nothing about the county.
export function individualNeedSentence(n) {
  if (!n) return "Individual risk could not be assessed.";
  if (n.need === NEED.UNKNOWN) return "Not enough yield history to describe this farm's own downside: " + n.reasons.join("; ") + ".";
  const bits = [];
  if (n.lossFreq80 != null) bits.push("below 80% of APH in " + Math.round(n.lossFreq80 * 100) + "% of " + n.yearsObserved + " years");
  if (n.lossSeverity80) bits.push("averaging " + Math.round(n.lossSeverity80 * 100) + "% short when it happens");
  if (n.worstYearFrac != null) bits.push("worst year " + Math.round(n.worstYearFrac * 100) + "% of APH");
  return NEED_LABEL[n.need] + " individual risk — " + bits.join(", ") + ".";
}
